import { Alignment, Displaying, Spacing, Typography } from "@bonusx-global/bx-design-system";
import Box from "@mui/material/Box";
import { useCallback, useEffect, useRef, useState } from "react";
import { AiChatbotIconContainer } from "../Chatbot/components/ai-chatbot-icon-container/ai-chatbot-icon-container";
import { AiChatbotIcon } from "../Chatbot/components/ai-chatbot-icon/ai-chatbot-icon";
import { TEXT_LABOR_ILLUSION_ID } from "./text-labor-illusion.const";
import type { ITextLaborIllusionText } from "./text-labor-illusion.interface";
import "./text-labor-illusion.style.css";

interface TextLaborIllusionProps {
  readonly messages: ITextLaborIllusionText[];
}

function TextLaborIllusion({ messages }: TextLaborIllusionProps): JSX.Element {
  const [currentMessageIndex, setCurrentMessageIndex] = useState<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const proceedToNextMessage = useCallback(() => {
    setCurrentMessageIndex(function updateMessageIndex(previousMessageIndex) {
      const nextMessageIndex = previousMessageIndex + 1;
      const reachedLastMessage = nextMessageIndex === messages.length;

      if (reachedLastMessage) {
        return previousMessageIndex;
      }

      const selectedMessage = messages[nextMessageIndex];
      timeoutRef.current = setTimeout(proceedToNextMessage, getRandomLaborIllusionMessageDuration(selectedMessage));
      return nextMessageIndex;
    });
  }, []);

  useEffect(function startLaborIllusionOnMount() {
    const initialMessage = messages[0];
    timeoutRef.current = setTimeout(proceedToNextMessage, getRandomLaborIllusionMessageDuration(initialMessage));

    return function stopLaborIllusionOnUnmount() {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Box display={Displaying.flex} alignItems={Alignment.center} gap={Spacing["750"]}>
      <AiChatbotIconContainer>
        <AiChatbotIcon />
      </AiChatbotIconContainer>
      <Typography variant="body1" color="textSecondary" id={TEXT_LABOR_ILLUSION_ID}>
        {messages[currentMessageIndex].message}
      </Typography>
    </Box>
  );
}

function getRandomLaborIllusionMessageDuration(message: ITextLaborIllusionText): number {
  const durationDelta = message.maxDurationInMilliseconds - message.minDurationInMilliseconds;
  return Math.random() * durationDelta + message.minDurationInMilliseconds;
}

export default TextLaborIllusion;
